import axiosInstance from '../../../core/services/axiosConfig';
import { apis } from '../../../core/services/params';
import React, { useState, useEffect } from "react";
import { Table, Button, Card, Badge, Typography, Tooltip, Empty, Tag, Modal, DatePicker } from "antd";
import type { TablePaginationConfig } from "antd";
import {
    ReloadOutlined,
    PhoneOutlined,
    RobotOutlined,
    ThunderboltOutlined,
    PlayCircleOutlined,
    DatabaseOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";

import StitchLayout from "../../components/Crm/CrmLayout";
import SaharaFilters from "./SaharaFilters";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SaharaCallTrack = () => {

    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        per_page: 10,
        total: 0,
    });

    const getInitialFilters = () => {
        try {
            const stored = sessionStorage.getItem('sahara_filters');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed.start_date) parsed.start_date = dayjs(parsed.start_date);
                if (parsed.end_date) parsed.end_date = dayjs(parsed.end_date);
                return parsed;
            }
        } catch (e) {
            // ignore
        }
        return {
            caller_phone: "",
            ucid: "",
            start_date: null,
            end_date: null,
            min_duration_sec: null,
        };
    };

    const [filters, setFilters] = useState<{
        caller_phone: string;
        ucid: string;
        start_date: any;
        end_date: any;
        min_duration_sec: number | null;
    }>(getInitialFilters());
    const [fetchTrigger, setFetchTrigger] = useState(0);
    // Download modal and loading state
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const navigate = useNavigate();
    // downloadRange holds Dayjs values from the RangePicker onChange
    const [downloadRange, setDownloadRange] = useState<any[]>([]);

    const handleUcidClick = (ucid: string) => {
        navigate(`/crm/ozonetel-call-track/${ucid}`);
    };

    const fetchCalls = async (page = 1, per_page = 10, currentFilters = filters) => {
        setLoading(true);
        try {
            // Clean filters to remove empty strings
            const activeFilters: any = {};
            if (currentFilters.caller_phone) activeFilters.caller_phone = currentFilters.caller_phone;
            if (currentFilters.ucid) activeFilters.ucid = currentFilters.ucid;
            if (currentFilters.min_duration_sec) activeFilters.min_duration_sec = currentFilters.min_duration_sec;

            if (currentFilters.start_date && currentFilters.end_date) {
                // Always clamp to 00:00:00 for start and 23:59:59 for end regardless of picker time
                activeFilters.start_date = currentFilters.start_date.startOf('day').format("YYYY-MM-DDTHH:mm:ss");
                activeFilters.end_date = currentFilters.end_date.endOf('day').format("YYYY-MM-DDTHH:mm:ss");
            }

            const response = await axiosInstance.post(
                `${apis.OZONETEL_VOICE_AI_AGENT}?page=${page}&per_page=${per_page}`,
                { filters: activeFilters }
            );
            const data = response.data;
            if (data.data) {
                setCalls(data.data);
                setPagination(data.pagination);
            }
        } catch (error) {
            console.error("Error fetching call data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCalls(pagination.page, pagination.per_page, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchTrigger]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
    ) => {
        fetchCalls(pagination.current, pagination.pageSize, filters);
    };

    const handleFilter = (newFilters: any) => {
        // Call fetchCalls directly to avoid stale state from async batching
        setFilters(newFilters);

        const toStore = { ...newFilters };
        if (toStore.start_date) toStore.start_date = toStore.start_date.toISOString();
        if (toStore.end_date) toStore.end_date = toStore.end_date.toISOString();
        sessionStorage.setItem('sahara_filters', JSON.stringify(toStore));

        setPagination(prev => ({ ...prev, page: 1 }));
        fetchCalls(1, pagination.per_page, newFilters);
    };

    const handleReset = () => {
        // Reset filters back to empty state
        const empty = {
            caller_phone: "",
            ucid: "",
            start_date: null,
            end_date: null,
            min_duration_sec: null,
        };
        setFilters(empty);
        sessionStorage.removeItem('sahara_filters');
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchCalls(1, pagination.per_page, empty);
    };

    // Fetch all records for selected date range and download as CSV
    const handleDownloadConfirm = async () => {
        if (!downloadRange || !downloadRange[0] || !downloadRange[1]) return;
        setDownloading(true);
        try {
            const response = await axiosInstance.post(
                `${apis.OZONETEL_VOICE_AI_AGENT}?page=1&per_page=5000`,
                {
                    filters: {
                        ...filters,
                        // Always use start of day and end of day for download date boundaries
                        start_date: downloadRange[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
                        end_date: downloadRange[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
                    }
                }
            );
            const records: any[] = response.data?.data || [];
            const headers = ['UCID', 'Caller Phone', 'Start Time', 'End Time', 'Duration (sec)', 'Turns', 'Tool Usage', 'Interruptions', 'Total Tokens'];
            const rows = records.map(c => [
                c.ucid, c.caller_phone,
                c.start_time ? moment(c.start_time).format('YYYY-MM-DD HH:mm:ss') : '',
                c.end_time ? moment(c.end_time).format('YYYY-MM-DD HH:mm:ss') : '',
                c.metrics?.total_duration_sec ?? '',
                c.metrics?.turn_count ?? '',
                c.metrics?.tool_use_count ?? '',
                c.metrics?.interruption_count ?? '',
                c.metrics?.token_usage?.total_tokens ?? '',
            ]);
            const csv = [headers, ...rows].map(r => r.map((v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `sahara_calls_${downloadRange[0].format('YYYYMMDD')}_to_${downloadRange[1].format('YYYYMMDD')}.csv`;
            a.click();
            URL.revokeObjectURL(url);
            setDownloadModalVisible(false);
        } catch (err) {
            console.error('Download failed', err);
        } finally {
            setDownloading(false);
        }
    };

    const columns = [
        {
            title: "UCID",
            dataIndex: "ucid",
            key: "ucid",
            width: 180,
            render: (text: string, record: any) => (
                <div className="flex flex-col items-start gap-1.5">
                    <Tooltip title="View Call Details">
                        <span
                            onClick={() => handleUcidClick(text)}
                            className="font-medium text-blue-600 cursor-pointer hover:text-blue-800 transition-colors font-mono text-xs"
                        >
                            {text}
                        </span>
                    </Tooltip>
                    {record.freshdesk_ticket_id && (
                        <a
                            href={`/crm/freshdesk-tickets/${record.freshdesk_ticket_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded shadow-sm hover:bg-blue-100 hover:text-blue-700 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                        >
                            🎫 {record.freshdesk_ticket_id}
                        </a>
                    )}
                </div>
            ),
        },
        {
            title: "Caller Phone",
            dataIndex: "caller_phone",
            key: "caller_phone",
            width: 160,
            render: (text: string) => (
                <div className="flex items-center gap-2 text-gray-600">
                    <PhoneOutlined className="text-gray-400" />
                    <a href={`tel:${text}`} className="font-mono text-xs hover:text-blue-600 hover:underline">
                        {text}
                    </a>
                </div>
            ),
        },
        {
            title: "Start Time",
            dataIndex: "start_time",
            key: "start_time",
            width: 140,
            render: (text: any) => (
                <div className="flex flex-col">
                    <span className="text-gray-800 text-xs font-medium">{moment(text).format("MMM DD, YYYY")}</span>
                    <span className="text-gray-400 text-[10px]">{moment(text).format("HH:mm:ss")}</span>
                </div>
            ),
        },
        {
            title: "End Time",
            dataIndex: "end_time",
            key: "end_time",
            width: 140,
            render: (text: any) => (
                <div className="flex flex-col">
                    <span className="text-gray-800 text-xs font-medium">{text ? moment(text).format("MMM DD, YYYY") : "-"}</span>
                    <span className="text-gray-400 text-[10px]">{text ? moment(text).format("HH:mm:ss") : ""}</span>
                </div>
            ),
        },
        {
            title: "Recording",
            dataIndex: "recording_url",
            key: "recording_url",
            width: 80,
            render: (url: string) => (
                url ? (
                    <Tooltip title="Play Recording">
                        <a href={url} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800">
                            <PlayCircleOutlined style={{ fontSize: '18px' }} />
                        </a>
                    </Tooltip>
                ) : (
                    <span className="text-gray-300">-</span>
                )
            ),
        },
        {
            title: "Duration",
            dataIndex: ["metrics", "total_duration_sec"],
            key: "duration",
            width: 100,
            render: (seconds: number) => {
                const duration = moment.utc(seconds * 1000).format("mm:ss");
                return <span className="font-mono text-gray-500 bg-gray-50 px-2 py-0.5 rounded border border-gray-100 text-xs">{duration}</span>;
            },
        },
        {
            title: "Turns",
            dataIndex: ["metrics", "turn_count"],
            key: "turns",
            width: 100,
            render: (count: number) => (
                <Badge count={count} showZero style={{ backgroundColor: '#52c41a' }} overflowCount={99} />
            ),
        },
        {
            title: "Tool Usage",
            dataIndex: ["metrics", "tool_use_count"],
            key: "tools",
            width: 120,
            render: (count: number) => (
                <Tag icon={<ThunderboltOutlined />} color={count > 0 ? "purple" : "default"}>
                    {count} Tools
                </Tag>
            ),
        },
        {
            title: "Token Usage",
            dataIndex: ["metrics", "token_usage"],
            key: "tokens",
            width: 150,
            render: (usage: any) => (
                <Tooltip title={
                    <div>
                        <div>Input: {usage?.input_tokens}</div>
                        <div>Output: {usage?.output_tokens}</div>
                    </div>
                }>
                    <div className="flex items-center gap-1 cursor-help">
                        <DatabaseOutlined className="text-gray-400" />
                        <span className="font-mono text-xs text-gray-600">
                            {((usage?.total_tokens || 0) / 1000).toFixed(1)}k
                        </span>
                        <span className="text-[10px] text-gray-400">tok</span>
                    </div>
                </Tooltip>
            ),
        },
        {
            title: "Interrupted",
            dataIndex: ["metrics", "interruption_count"],
            key: "interruption",
            width: 120,
            render: (count: number) => (
                count > 0 ?
                    <Badge status="error" text={<span className="text-xs text-red-500">{count} times</span>} /> :
                    <Badge status="success" text={<span className="text-xs text-gray-400">None</span>} />
            ),
        }
    ];

    return (
        <StitchLayout>
            <div className="space-y-6 max-w-[1600px] mx-auto">
                {/* Page Header */}
                <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600 text-2xl">
                            <RobotOutlined />
                        </div>
                        <div>
                            <Title level={3} style={{ marginBottom: 0, letterSpacing: '-0.5px' }}>Sahara Voice AI</Title>
                            <Text type="secondary">Automated agent interactions and performance metrics</Text>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        {/* Download Report opens modal with date range picker */}
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => setDownloadModalVisible(true)}
                        >
                            Download Report
                        </Button>
                        <Button icon={<ReloadOutlined />} onClick={() => setFetchTrigger(c => c + 1)}>Refresh</Button>
                    </div>
                </div>

                {/* New collapsible filters component for Sahara */}
                <SaharaFilters
                    onFilter={handleFilter}
                    onReset={handleReset}
                    loading={loading}
                    initialValues={filters}
                />

                {/* Table Section */}
                <Card bordered={false} className="shadow-sm rounded-xl overflow-hidden" bodyStyle={{ padding: 0 }}>
                    <Table
                        columns={columns as any}
                        dataSource={calls}
                        loading={loading}
                        rowKey="ucid"
                        pagination={{
                            current: pagination.page,
                            pageSize: pagination.per_page,
                            total: pagination.total,
                            showSizeChanger: true,
                            showTotal: (total, range) => <span className="text-gray-500 mx-4">{`${range[0]}-${range[1]} of ${total} calls`}</span>,
                            className: "p-4"
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                        size="middle"
                        locale={{
                            emptyText: <Empty description="No Voice AI calls found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        }}
                    />
                </Card>
            </div>

            {/* Download report modal with date range selector */}
            <Modal
                title={<span className="flex items-center gap-2"><DownloadOutlined /> Download Report</span>}
                open={downloadModalVisible}
                onCancel={() => setDownloadModalVisible(false)}
                onOk={handleDownloadConfirm}
                okText={downloading ? "Downloading…" : "Download CSV"}
                okButtonProps={{ loading: downloading, icon: <DownloadOutlined /> }}
                cancelButtonProps={{ disabled: downloading }}
                width={420}
            >
                {/* Date range picker defaulting to today */}
                <div className="py-2">
                    <p className="text-sm text-gray-500 mb-3">Select a date range to download all matching call records.</p>
                    <RangePicker
                        style={{ width: '100%' }}
                        onChange={(vals) => setDownloadRange(vals ?? [])}
                        disabledDate={(d) => d && d.format('YYYY-MM-DD') > moment().format('YYYY-MM-DD')}
                    />
                </div>
            </Modal>
        </StitchLayout>
    );
};

export default SaharaCallTrack;
